import React, { useCallback, useEffect, useState } from "react";
import CloseIcon from "../Assets/svgs/CloseIcon";
import CsvIcon from "../Assets/svgs/CsvIcon";
import { DoubleArrowLeftIcon } from "../Assets/svgs/DoubleArrowLeftIcon";
import { DoubleArrowRightIcon } from "../Assets/svgs/DoubleArrowRightIcon";
import ExcelIcon from "../Assets/svgs/ExcelIcon";
import { SingleArrowLeftIcon } from "../Assets/svgs/SingleArrowLeftIcon";
import { SingleArrowRightIcon } from "../Assets/svgs/SingleArrowRightIcon";
import { debounce } from "../Common/debounce";
import EditableTable from "../Common/EditableTable";
import useDebounce from "../Common/useDebounce";
import { toObjectKey, uuidGenerator } from "../Common/utils";
import { Loader } from "../Common/Loader";

export const EditableCSVContactDataModal = ({
  fileName,
  isCsvFile,
  excelData,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    columns: excelData?.columns,
    data: excelData?.data,
  });
  const [isLoading, setIsLoading] = useState(false);

  const { columns, data } = formData;
  const pageLimit = 15;
  const searchKey = "Name";
  const [searchValue, setSearchValue] = useState("");
  const searchQuery = useDebounce(searchValue, 1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsData, setRowsData] = useState([]);
  const [totalItems, setTotalItems] = useState(
    Array.isArray(data) ? data?.length : 0
  );
  const currentData = {
    columns: columns,
    data: rowsData,
  };
  const [inputPage, setInputPage] = useState(currentPage);
  const [totalPages, setTotalPages] = useState(
    Array.isArray(data) && data?.length
      ? Math.ceil(data?.length / pageLimit)
      : 0
  );

  const itemsPerPage = totalItems > pageLimit ? pageLimit : totalItems;
  const previousPage = currentPage > 1 ? currentPage - 1 : null;
  const nextPage = currentPage < totalPages ? currentPage + 1 : null;
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
      const indexOfLastItem = page * itemsPerPage;
      const indexOfFirstItem = indexOfLastItem - itemsPerPage;
      if (searchQuery && searchQuery.length) {
        const filtredRow = (Array.isArray(data) ? data : [])
          ?.filter((row) => {
            if (
              row[searchKey] &&
              String(row[searchKey])
                .toLocaleLowerCase()
                .includes(String(searchQuery).toLocaleLowerCase())
            ) {
              return true;
            } else {
              return false;
            }
          })
          .slice(indexOfFirstItem, indexOfLastItem);
        setRowsData(filtredRow);
      } else {
        setRowsData(
          Array.isArray(data)
            ? data.slice(indexOfFirstItem, indexOfLastItem)
            : []
        );
      }
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    const halfMaxPagesToShow = Math.floor(maxPagesToShow / 2);

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= halfMaxPagesToShow) {
        for (let i = 1; i <= maxPagesToShow; i++) {
          pageNumbers.push(i);
        }
      } else if (currentPage + halfMaxPagesToShow >= totalPages) {
        for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        for (
          let i = currentPage - halfMaxPagesToShow;
          i <= currentPage + halfMaxPagesToShow;
          i++
        ) {
          pageNumbers.push(i);
        }
      }
    }

    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();

  const onSearchCustomer = () => {
    if (Array.isArray(columns) && columns.length > 0) {
      const filtredAllRows = (Array.isArray(data) ? data : [])?.filter(
        (row) => {
          if (!columns?.includes(searchKey) || !searchValue) {
            return true;
          } else if (
            row[searchKey] &&
            String(row[searchKey])
              .toLocaleLowerCase()
              .includes(String(searchQuery).toLocaleLowerCase())
          ) {
            return true;
          } else {
            return false;
          }
        }
      );
      const totalItems = Array.isArray(filtredAllRows)
        ? filtredAllRows?.length
        : 0;
      const itemsPerPage = totalItems > pageLimit ? pageLimit : totalItems;
      const indexOfLastItem = 1 * itemsPerPage;
      const indexOfFirstItem = indexOfLastItem - itemsPerPage;
      setRowsData(() => {
        const filtredRow = filtredAllRows.slice(
          indexOfFirstItem,
          indexOfLastItem
        );
        return filtredRow;
      });
      setTotalPages(
        Array.isArray(filtredAllRows) && filtredAllRows?.length
          ? Math.ceil(filtredAllRows?.length / pageLimit)
          : 0
      );
      setTotalItems(totalItems);
    }
  };

  const onChangeData = ({ columns, data }) => {
    let updatedLabelsColumn = [];
    const newUpdatedColumns = columns?.map((column) => {
      const isLabelChanged = formData.columns?.find(
        (item) => item?.id == column?.id && item?.label != column?.label
      );
      if (isLabelChanged) {
        const newKey = toObjectKey(column?.label);
        updatedLabelsColumn.push({ ...column, accessor: newKey });
        return {
          ...column,
          id: newKey,
          accessor: newKey,
        };
      }
      return column;
    });
    setFormData((prev) => {
      let newRows = [];
      if (data?.length) {
        const updatedCurrentPageData = data?.map((item) => {
          let newItem = {
            ...item,
            id: item?.id ? item.id : uuidGenerator(),
          };
          if (!item?.id) {
            newRows.push(newItem);
          }
          if (updatedLabelsColumn?.length) {
            updatedLabelsColumn.forEach((column) => {
              if (column?.id in newItem) {
                newItem[column?.accessor] = newItem[column?.id];
                delete newItem[column?.id];
              }
            });
          }
          return newItem;
        });
        setRowsData(updatedCurrentPageData);
      }

      const updatedData = prev.data?.map((item) => {
        const currentDataItem = data?.find(
          (data_item) => data_item?.id == item?.id
        );
        return currentDataItem ? currentDataItem : item;
      });
      return {
        columns: newUpdatedColumns,
        data: [...newRows, ...updatedData]?.map((item) => {
          let newItem = {
            ...item,
          };
          if (updatedLabelsColumn?.length) {
            updatedLabelsColumn.forEach((column) => {
              if (column?.id in newItem) {
                newItem[column?.accessor] = newItem[column?.id];
                delete newItem[column?.id];
              }
            });
          }
          return newItem;
        }),
      };
    });
  };

  const onSaveData = () => {
    setIsLoading(true);
    onSave(formData);
    setTimeout(() => {
      setIsLoading(false);
    }, 800);
  };

  useEffect(() => {
    onSearchCustomer();
    setCurrentPage(1);
  }, [searchQuery]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="modal modal--previewFileCsv modal--open">
      <div className="modal__dialog">
        <div className="modal__content">
          <div className="modal__header">
            <h4 className="modal__header--title">
              Preview {isCsvFile ? "CSV" : "Excel file"}
            </h4>
            <button className="btn btn__transparent btnClose" onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
          <div className="modal__body ">
            <div className="tableWrap">
              <div className="tableWrap__head tableWrap__head--wrap">
                <div className="formRow">
                  <div className="formCol">
                    <span className="form__label form__label--csv">
                      {isCsvFile ? (
                        <CsvIcon height={"27"} width={"27"} />
                      ) : (
                        <ExcelIcon height={"27"} width={"27"} />
                      )}
                      {fileName ?? ""}
                    </span>
                  </div>
                </div>
                <div className="btn__group">
                  <button className="btn btn__primary" onClick={onSaveData}>
                    Save
                  </button>
                </div>
              </div>
              <div className="tableContainer">
                {Array.isArray(excelData?.columns) &&
                excelData?.columns?.length ? (
                  <div style={{ overflow: "auto", display: "flex" }}>
                    <div
                      style={{
                        flex: "1 1 auto",
                      }}
                    >
                      <EditableTable
                        data={currentData}
                        onChangeData={onChangeData}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="no-data">No Submissions Found!</div>
                )}
              </div>
              <div className="pagination">
                <div className="pagination__result">
                  {startIndex}-{endIndex} of {totalItems} results
                </div>
                <nav
                  aria-label="pagination navigation"
                  className="MuiPagination-root MuiPagination-outlined css-1oj2twp-MuiPagination-root"
                >
                  <ul className="MuiPagination-ul css-wjh20t-MuiPagination-ul">
                    <li>
                      <button
                        className="MuiButtonBase-root Mui-disabled MuiPaginationItem-root MuiPaginationItem-sizeMedium MuiPaginationItem-outlined MuiPaginationItem-rounded Mui-disabled MuiPaginationItem-firstLast css-19xm0h7-MuiButtonBase-root-MuiPaginationItem-root"
                        type="button"
                        aria-label="Go to first page"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(1)}
                      >
                        <DoubleArrowLeftIcon />
                      </button>
                    </li>
                    <li>
                      <button
                        className="MuiButtonBase-root Mui-disabled MuiPaginationItem-root MuiPaginationItem-sizeMedium MuiPaginationItem-outlined MuiPaginationItem-rounded Mui-disabled MuiPaginationItem-previousNext css-19xm0h7-MuiButtonBase-root-MuiPaginationItem-root"
                        type="button"
                        aria-label="Go to previous page"
                        disabled={!previousPage}
                        onClick={() => handlePageChange(previousPage ?? 1)}
                      >
                        <SingleArrowLeftIcon />
                      </button>
                    </li>
                    {pageNumbers?.map((page, index) => (
                      <li key={index}>
                        <button
                          className={`MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-sizeMedium MuiPaginationItem-outlined MuiPaginationItem-rounded MuiPaginationItem-page css-19xm0h7-MuiButtonBase-root-MuiPaginationItem-root ${
                            currentPage === page ? "Mui-selected" : ""
                          }`}
                          type="button"
                          aria-current={
                            currentPage === page ? "true" : undefined
                          }
                          aria-label={`Go to page ${page}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                          <span className="MuiTouchRipple-root css-8je8zh-MuiTouchRipple-root"></span>
                        </button>
                      </li>
                    ))}
                    {totalPages > 5 && currentPage + 2 < totalPages && (
                      <li>
                        <span className="pagination-ellipsis">...</span>
                      </li>
                    )}
                    {totalPages > 5 && currentPage < totalPages - 2 && (
                      <li>
                        <button
                          className="MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-sizeMedium MuiPaginationItem-outlined MuiPaginationItem-rounded MuiPaginationItem-page css-19xm0h7-MuiButtonBase-root-MuiPaginationItem-root"
                          type="button"
                          aria-label={`Go to page ${totalPages}`}
                          onClick={() => handlePageChange(totalPages)}
                        >
                          {totalPages}
                          <span className="MuiTouchRipple-root css-8je8zh-MuiTouchRipple-root"></span>
                        </button>
                      </li>
                    )}
                    <li>
                      <button
                        className="MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-sizeMedium MuiPaginationItem-outlined MuiPaginationItem-rounded MuiPaginationItem-previousNext css-19xm0h7-MuiButtonBase-root-MuiPaginationItem-root"
                        type="button"
                        aria-label="Go to next page"
                        disabled={!nextPage}
                        onClick={() => handlePageChange(nextPage ?? totalPages)}
                      >
                        <SingleArrowRightIcon />
                        <span className="MuiTouchRipple-root css-8je8zh-MuiTouchRipple-root"></span>
                      </button>
                    </li>
                    <li>
                      <button
                        className="MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-sizeMedium MuiPaginationItem-outlined MuiPaginationItem-rounded MuiPaginationItem-firstLast css-19xm0h7-MuiButtonBase-root-MuiPaginationItem-root"
                        type="button"
                        aria-label="Go to last page"
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(totalPages)}
                      >
                        <DoubleArrowRightIcon />
                        <span className="MuiTouchRipple-root css-8je8zh-MuiTouchRipple-root"></span>
                      </button>
                    </li>
                  </ul>
                </nav>
                <div className="pagination__actions">
                  <h6>Go to page</h6>
                  <input
                    type="number"
                    className="formField__input sm"
                    value={inputPage}
                    onChange={(e) => {
                      const page = parseInt(e.target.value, 15);
                      if (!isNaN(page) && page > 0 && page <= totalPages) {
                        setInputPage(page);
                      } else {
                        setInputPage("");
                      }
                    }}
                  />
                  <button
                    className="btn btn__primary"
                    onClick={() => handlePageChange(inputPage)}
                  >
                    Go
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal__backdrop"></div>
    </div>
  );
};
